<template>
  <div>
    <div class="h3 mb-4">Основная информация</div>

    <div v-if="!isQuestionnaireSent" class="paragraph-s_h text-secondary">
      <span class="text-red">*</span> — поля обязательные для заполнения
    </div>

    <e-form v-if="!isQuestionnaireSent" ref="formRef" class="common-information__form mt-10">
      <multilanguage-field
        v-model="form.name"
        :rules="[required()]"
        :common-rules="[maxLength(255)]"
        label="Наименование"
        class="mb-4"
      />

      <e-combobox
        v-model="form.chain"
        label="Сеть отелей"
        clearable
        :items="dictionaries?.commonInformation.hotelChains || []"
      />

      <inputs-group>
        <e-input v-model="form.address" label="Адрес" disabled class="mt-8" :rules="[required()]" />

        <e-input
          v-model:number-value="form.buirdYear"
          class="inputs-group__tiny-input"
          :maska="yearMask"
          :rules="[
            required(),
            minLength(4, 'Некорректный год постройки'),
            min(1700, 'Некорректный год постройки'),
            max(new Date().getFullYear(), 'Некорректный год постройки'),
          ]"
        >
          <template #label> Год постройки объекта<span class="text-red">*</span> </template>
        </e-input>
        <e-input
          v-model:number-value="form.reconstructionYear"
          class="inputs-group__tiny-input"
          :maska="yearMask"
          :rules="[
            minLength(4, 'Некорректный год реконструкции'),
            min(
              form.buirdYear || 1700,
              form.buirdYear ? 'Не может быть меньше года постройки' : 'Некорректный год реконструкции',
            ),
            max(new Date().getFullYear(), 'Некорректный год реконструкции'),
          ]"
        >
          <template #label> Год реконструкции объекта </template>
        </e-input>
        <e-input
          v-model:number-value="form.territoryArea"
          class="inputs-group__tiny-input"
          :rules="[required(), max(99999.99)]"
          :maska="{
            number: {
              locale: 'ru',
              fraction: 2,
              unsigned: true,
            },
          }"
          :type="'text'"
          :maxlength="9"
        >
          <template #label> Площадь территории, га<span class="text-red">*</span> </template>
        </e-input>
      </inputs-group>

      <inputs-group title="Ближайшие транспортные узлы" class="mt-10">
        <e-input v-model="form.airport" label="Ближайший аэропорт (город, название)" :rules="[maxLength(100)]" />
        <e-input v-model="form.railwayStation" label="Ближайшая ж/д станция (название)" :rules="[maxLength(100)]" />
        <e-input v-model="form.metroStation" label="Ближайшая станция метро" :rules="[maxLength(100)]" />
      </inputs-group>

      <inputs-group title="Заезд / выезд" class="mt-10">
        <e-time-picker v-model="form.checkIn" class="inputs-group__tiny-input">
          <template #label> Время заезда </template>
        </e-time-picker>
        <e-time-picker v-model="form.checkOut" class="inputs-group__tiny-input">
          <template #label> Время выезда </template>
        </e-time-picker>
      </inputs-group>

      <inputs-group title="Заезд с животными" required class="mt-10">
        <e-radio-group
          v-model="form.pets"
          inline
          :items="petsOptions"
          :rules="[isDefined()]"
          @update:model-value="onUpdatePets"
        />
        <e-input
          v-model:number-value="form.petWeightLimitKg"
          type="number"
          class="inputs-group__tiny-input"
          label="Ограничение по весу, кг"
          :disabled="form.pets === 'Запрещён'"
          :rules="[maxLength(3)]"
          :maska="'#'.repeat(4)"
        />
      </inputs-group>

      <inputs-group title="Документы для заезда взрослого" required class="mt-10">
        <combobox-with-groups
          v-model="form.documentsAdult"
          label="Добавить"
          :groups="getGroupsForType('adult')"
          :rules="[required()]"
        />
      </inputs-group>

      <inputs-group required class="mt-10" narrow-gap>
        <e-checkbox 
          v-model="form.acceptChildren"
          @update:modelValue="onAcceptChildrenUpdate"
        />
        <inputs-group title="Документы для заезда ребенка" required no-margin-bottom>
        <template #subtitle>
          Если вы не принимаете детей, то снимите выбор с этого раздела
        </template>
        </inputs-group>

        <combobox-with-groups
          v-if="form.acceptChildren"
          v-model="form.documentsChildren"
          label="Добавить"
          :groups="getGroupsForType('children')"
          :rules="form.acceptChildren ? [required()] : []"
        />
      </inputs-group>

      <inputs-group title="Документы для заезда иностранного гражданина" required class="mt-10">
        <combobox-with-groups
          v-model="form.documentsForeigner"
          label="Добавить"
          :groups="getGroupsForType('foreigner')"
          :rules="[required()]"
        />
      </inputs-group>

      <inputs-group title="Описание комплекса" required class="mt-10">
        <template #subtitle>
          Краткое описание объекта размещения, его местонахождение, удаленность от городской/курортной инфраструктуры,
          особенности территории (расположение корпусов, их этажность, удаленность друг от друга, наличие лифта в
          корпусах)
        </template>

        <e-textarea v-model="form.description" rows="10" label="Описание" :rules="[required(), maxLength(500)]" />
      </inputs-group>

      <inputs-group title="Контакты" class="mt-10">
        <template #subtitle>
          Отдел контента может использовать информацию на вашем сайте для заполнения карточки объекта размещения.
        </template>

        <e-input v-model="form.website" label="Официальный сайт" :rules="[URLRule(), maxLength(100)]" />
        <e-input v-model="form.contactPerson" :rules="[required(), maxLength(150)]">
          <template #label> ФИО контактного лица<span class="text-red">*</span> </template>
        </e-input>
        <e-input v-model="form.contactEmail" :rules="[required(), emailRule(), maxLength(100)]">
          <template #label> Email контактного лица<span class="text-red">*</span> </template>
        </e-input>
      </inputs-group>

      <inputs-group title="Фотографии объекта размещения" required class="mt-10">
        <template #subtitle>
          Загрузите главное изображение, фото здания и территории или укажите ссылку на фотобанк. Отдел контента может
          использовать изображения из вашего фотобанка для заполнения карточки объекта размещения
        </template>

        <e-alert v-if="formRef?.isDirty && !form.images.length && !form.imagesLink" variant="error" thin class="w-100">
          <div class="paragraph-m_h font-500">Не загружены фотографии и не указана ссылка на фотобанк</div>
        </e-alert>

        <content-images-uploader
          ref="imagesUploaderRef"
          v-model="form.images"
          v-model:images-link="form.imagesLink"
          v-model:attachments="form.attachments"
          :content-section="ContentSection.CommonInformation"
          @uploaded="saveIfHasChanges"
        />
      </inputs-group>

      <div class="d-flex">
        <e-btn
          type="submit"
          variant="primary"
          class="mt-10 mb-4 ml-auto"
          append-icon-src="arrowRight"
          :loading="isLoading"
          @click="next(true)"
          >Далее</e-btn
        >
      </div>
    </e-form>

    <div v-else class="mt-10">
      <property-item title="Наименование">
        <multilanguage-text :text="form.name" mode="all">
          <template #default="{ value }">
            <div class="paragraph-m_h font-600">{{ value }}</div>
          </template>
        </multilanguage-text>
      </property-item>
      <property-item v-if="form.chain" title="Сеть отелей" class="mt-4 mr-4">
        {{ form.chain }}
      </property-item>

      <property-item v-if="form.address" title="Адрес " class="mt-10">
        {{ form.address }}
      </property-item>

      <div class="d-flex mt-4">
        <property-item v-if="form.buirdYear" title="Год постройки объекта">
          {{ form.buirdYear }}
        </property-item>
        <property-item v-if="form.reconstructionYear" title="Год реконструкции объекта" class="ml-10">
          {{ form.reconstructionYear }}
        </property-item>
      </div>

      <property-item v-if="form.territoryArea" title="Площадь территории (га)" class="mt-4">
        {{ form.territoryArea }}
      </property-item>

      <div v-if="form.airport || form.railwayStation || form.metroStation">
        <div class="mt-10 paragraph-m_h font-600">Ближайшие транспортные узлы</div>

        <property-item v-if="form.airport" title="Ближайший аэропорт (город, название)" class="mt-4">
          {{ form.airport }}
        </property-item>

        <property-item v-if="form.railwayStation" title="Ближайшая ж/д станция (название)" class="mt-4">
          {{ form.railwayStation }}
        </property-item>

        <property-item v-if="form.metroStation" title="Ближайшая станция метро" class="mt-4">
          {{ form.metroStation }}
        </property-item>
      </div>

      <div v-if="form.checkIn || form.checkOut">
        <div class="mt-10 paragraph-m_h font-600">Заезд / выезд</div>

        <div class="d-flex mt-4">
          <property-item v-if="form.checkIn" title="Время заезда">
            {{ getTimeFromPTH(form.checkIn) }}
          </property-item>
          <property-item v-if="form.checkOut" title="Время выезда" class="ml-10">
            {{ getTimeFromPTH(form.checkOut) }}
          </property-item>
        </div>
      </div>

      <div class="mt-10 mb-4 paragraph-m_h font-600">Заезд с животными</div>

      <e-radio-group v-model="form.pets" inline disabled :items="petsOptions" />

      <div class="mt-4">
        <property-item v-if="form.petWeightLimitKg" title="Ограничение по весу, кг">
          {{ form.petWeightLimitKg }}
        </property-item>
      </div>

      <div class="mt-10 paragraph-m_h font-600">Документы для заезда взрослого</div>

      <template v-if="form.documentsAdult.length">
        <e-chip v-for="document in form.documentsAdult" :key="document" class="mt-2 mr-2">
          {{ document }}
        </e-chip>
      </template>

      <template v-if="form.documentsChildren.length">
        <div class="mt-10 paragraph-m_h font-600">Документы для заезда ребенка</div>

        <e-chip v-for="document in form.documentsChildren" :key="document" class="mt-2 mr-2">
          {{ document }}
        </e-chip>
      </template>
      <template v-else="form.documentsChildren.length">
          <div class="d-flex mt-10">
            <e-checkbox 
            v-model="form.acceptChildren"
            disabled
          />
        
          <div class="paragraph-m_h font-600 text-inactive">Документы для заезда ребенка</div>
        </div>
      </template>

      <div class="mt-10 paragraph-m_h font-600">Документы для заезда иностранного гражданина</div>

      <template v-if="form.documentsForeigner.length">
        <e-chip v-for="document in form.documentsForeigner" :key="document" class="mt-2 mr-2">
          {{ document }}
        </e-chip>
      </template>

      <div class="mt-10 paragraph-m_h font-600">Описание комплекса</div>

      <property-item v-if="form.description" title="Описание" class="mt-4">
        {{ form.description }}
      </property-item>

      <div class="mt-10 paragraph-m_h font-600">Контакты</div>

      <property-item v-if="form.website" title="Официальный сайт" class="mt-4">
        {{ form.website }}
      </property-item>
      <property-item v-if="form.contactPerson" title="ФИО контактного лица" class="mt-4">
        {{ form.contactPerson }}
      </property-item>
      <property-item v-if="form.contactEmail" title="Email контактного лица" class="mt-4">
        {{ form.contactEmail }}
      </property-item>

      <div v-if="form.images.length || form.imagesLink" class="mt-10 paragraph-m_h font-600">
        Фотографии объекта размещения
      </div>

      <images-gallery v-if="form.images.length" class="mt-4" :files="form.images" />

      <property-item v-if="form.imagesLink" title="Главное изображение" class="mt-4">
        {{ form.imagesLink }}
      </property-item>
      <div class="d-flex">
        <e-btn type="submit" variant="primary" class="mt-10 mb-4 ml-auto" append-icon-src="arrowRight" @click="nextStep"
          >Далее</e-btn
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, reactive, ref, toRef, watch } from 'vue';
import type { EFormType } from '@/core/types/components/components';
import type { LoadedFile } from '@/core/types/files';
import ComboboxWithGroups from '@/core/components/interface/ComboboxWithGroups.vue';
import InputsGroup from '@/core/components/interface/InputsGroup.vue';
import PropertyItem from '@/core/components/interface/PropertyItem.vue';
import {
  EAlert,
  EBtn,
  ECheckbox,
  EChip,
  ECombobox,
  EForm,
  EInput,
  ERadioGroup,
  ETextarea,
  ETimePicker,
  MultilanguageField,
  MultilanguageText,
} from '@/core/components/ui';
import ImagesGallery from '@/core/components/ui/files/filesGallery.vue';
import { yearMask } from '@/core/consts/masks';
import { getTimeFromPTH } from '@/core/utils/date';
import { compareObjects } from '@/core/utils/objects/get-delta';
import { emailRule, isDefined, max, maxLength, min, minLength, required, URLRule } from '@/core/utils/validation';
import { QuestionnaireMasterStep } from '@/graphql/generated';
import ContentImagesUploader from '@/parts/content/components/ContentImagesUploader.vue';
import { useContentStore } from '@/parts/content/store';
import { ContentSection } from '@/parts/content/types';
import { useContentImagesUpload } from '@/parts/content/utils';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';

const { accommodationObject } = useAccommodationObjectsStore();
const {
  dictionaries,
  questionnaireAnswers,
  saveQuestionnaire,
  saveQuestionnaireDebounced,
  moveToNextStep,
  isQuestionnaireSent,
  nextStep,
  needValidate,
  completeValidation,
} = useContentStore();
const { initImages } = useContentImagesUpload(toRef(() => accommodationObject.value?.cid));

const formRef = ref<EFormType>();
const imagesUploaderRef = ref();

const generalInfo = toRef(() => questionnaireAnswers.value?.generalInfo);

const form = reactive({
  name: generalInfo.value?.name || [],
  address: generalInfo.value?.address ?? null,
  chain: generalInfo.value?.hotelChain ?? null,
  buirdYear: generalInfo.value?.buildYear ?? null,
  reconstructionYear: generalInfo.value?.renovationYear ?? null,
  territoryArea: generalInfo.value?.totalArea ?? null,
  airport: generalInfo.value?.nearestAeroport ?? null,
  railwayStation: generalInfo.value?.nearestTrainStation ?? null,
  metroStation: generalInfo.value?.nearestSubwayStation ?? null,
  checkIn: generalInfo.value?.checkIn ?? null,
  checkOut: generalInfo.value?.checkOut ?? null,
  pets: generalInfo.value?.petsAllowed ?? null,
  petWeightLimitKg: generalInfo.value?.petWeightLimitKg ?? null,
  documentsAdult: generalInfo.value?.requiredDocumentsAdult || [],
  acceptChildren: generalInfo.value?.isAcceptChildren ?? true,
  documentsChildren: generalInfo.value?.requiredDocumentsChildren || [],
  documentsForeigner: generalInfo.value?.requiredDocumentsForeigner || [],
  description: generalInfo.value?.description ?? null,
  website: generalInfo.value?.webSite ?? null,
  contactPerson: generalInfo.value?.contactFullName ?? null,
  contactEmail: generalInfo.value?.contactEmail ?? null,
  images: [] as LoadedFile[],
  attachments: generalInfo.value?.attachments || [],
  imagesLink: generalInfo.value?.photobankUrl,
});

void initImages(form.attachments, form.images, ContentSection.CommonInformation);

const save = () => saveQuestionnaireDebounced(mapAnswersToSave());
const saveIfHasChanges = async () => {
  if (hasChanges()) {
    await saveQuestionnaire(mapAnswersToSave());
  }
};

watch(
  form,
  () => {
    void save();
  },
  { deep: true },
);

const onUpdatePets = (value: string) => {
  if (value === 'Запрещён') {
    form.petWeightLimitKg = null;
  }
};

const isLoading = ref(false);

const validate = async () => {
  const isFormValid = await formRef.value?.validate();
  if (!isFormValid) return false;

  if (imagesUploaderRef.value && !imagesUploaderRef.value.validate()) {
    return false;
  }

  return true;
};

const hasChanges = () => {
  if (questionnaireAnswers.value?.generalInfo) {
    delete questionnaireAnswers.value.generalInfo.__typename;

    if (!compareObjects(mapAnswersToSave().generalInfo, questionnaireAnswers.value.generalInfo)) {
      return true;
    }
  }

  return false;
};

const next = async (force = false) => {
  if (!force && !hasChanges()) {
    return true;
  }

  const isValid = await validate();
  if (!isValid) {
    return false;
  }

  isLoading.value = true;
  await moveToNextStep(QuestionnaireMasterStep.GeneralInfo, mapAnswersToSave());
  isLoading.value = false;

  return true;
};

const getGroupsForType = (type: 'adult' | 'children' | 'foreigner') => {
  const docs = dictionaries.value?.commonInformation?.checkinDocuments;
  if (!docs) return [];

  const groups = docs[`${type}Documents`];
  if (!Array.isArray(groups)) return [];

  return groups.map((group) => ({
    groupName: group.groupName,
    items: group.documents || [],
  }));
};

const petsOptions = [
  {
    title: 'Разрешён',
    value: 'Разрешён',
  },
  {
    title: 'Запрещён',
    value: 'Запрещён',
  },
  {
    title: 'Разрешён за дополнительную плату',
    value: 'Разрешён за дополнительную плату',
  },
];

const onAcceptChildrenUpdate = (status: boolean) => {
  form.acceptChildren = status;
  if (!status) form.documentsChildren = [];
};

onMounted(async () => {
  if (needValidate.value) {
    await validate();
    void completeValidation();
  }
});

onBeforeUnmount(async () => {
  await saveIfHasChanges();
});

function mapAnswersToSave() {
  return {
    generalInfo: {
      name: form.name,
      address: form.address,
      hotelChain: form.chain,
      buildYear: form.buirdYear,
      renovationYear: form.reconstructionYear,
      totalArea: form.territoryArea,
      nearestAeroport: form.airport,
      nearestTrainStation: form.railwayStation,
      nearestSubwayStation: form.metroStation,
      checkIn: form.checkIn,
      checkOut: form.checkOut,
      petsAllowed: form.pets,
      petWeightLimitKg: form.petWeightLimitKg,
      requiredDocumentsAdult: form.documentsAdult.filter((doc) => typeof doc === 'string'),
      isAcceptChildren: form.acceptChildren,
      requiredDocumentsChildren: form.documentsChildren.filter((doc) => typeof doc === 'string'),
      requiredDocumentsForeigner: form.documentsForeigner.filter((doc) => typeof doc === 'string'),
      description: form.description,
      webSite: form.website,
      contactFullName: form.contactPerson,
      contactEmail: form.contactEmail,
      attachments: form.attachments,
      photobankUrl: form.imagesLink,
    },
  };
}
</script>

<style lang="scss" scoped></style>
